import { get as lodashGet } from "lodash-es";
import { useSearchStore } from "../store/useSearchStore";
import { useKinds } from "@certa/queries/hooks/kinds.hooks";
import { useGetDataObjectOptions } from "./useGetDataObjectOptions";
import { useGetObjectTypeTranslations } from "@certa/queries/hooks/dataObjects.hooks";
import { useInstanceConfigQuery } from "@certa/queries/hooks/config.hook";

export const usePreSelectedWorkflowKindTags = () => {
  const { data: config } = useInstanceConfigQuery();
  const preSelectedKindTags: string[] = lodashGet(
    config,
    "es_configuration.client_config.searchable_workflow_kind_tags",
    []
  );

  return preSelectedKindTags;
};

export const useExcludedWorkflowKindTags = () => {
  const { data: config } = useInstanceConfigQuery();
  const excludedWorkflowKindTags: string[] = lodashGet(
    config,
    "es_configuration.client_config.exclude_workflow_kind_tags",
    []
  );
  return excludedWorkflowKindTags;
};

export const usePreSelectedWorkflowDataObjectTags = () => {
  const { data: config } = useInstanceConfigQuery();
  const preSelectedDataObjectTags: string[] = lodashGet(
    config,
    "es_configuration.client_config.searchable_object_type_tags",
    []
  );

  return preSelectedDataObjectTags;
};

export const useExcludedWorkflowDataObjectTags = () => {
  const { data: config } = useInstanceConfigQuery();
  const excludedWorkflowKindTags: string[] = lodashGet(
    config,
    "es_configuration.client_config.exclude_object_type_tags",
    []
  );
  return excludedWorkflowKindTags;
};

export const usePreSelectedWorkflowKinds = () => {
  const {
    setFilters,
    hasPrefilledKinds,
    setHasPrefilledKinds,
    setPreSelectedKindIds
  } = useSearchStore();
  const { data: kindsList } = useKinds();
  const excludedWorkflowKindTags = useExcludedWorkflowKindTags();
  const preSelectedKindTags = usePreSelectedWorkflowKindTags();

  // Only process pre-selected workflow kinds if:
  // 1. We haven't prefilled kinds yet
  // 2. We have kind data available
  // 3. We have pre-selected tags to apply
  if (
    !hasPrefilledKinds &&
    kindsList?.length &&
    preSelectedKindTags.length > 0
  ) {
    // Filter out excluded kinds and get IDs of pre-selected kinds
    const preSelectedKindIds = kindsList
      .filter(
        kind =>
          !excludedWorkflowKindTags.includes(kind.tag) &&
          preSelectedKindTags.includes(kind.tag)
      )
      .map(kind => String(kind.id));

    // Only set filters if we have IDs to set
    if (preSelectedKindIds.length > 0) {
      setFilters({ kindIds: preSelectedKindIds });
    }

    setPreSelectedKindIds(preSelectedKindIds);

    // Mark as initialized so we don't run this logic again
    setHasPrefilledKinds(true);
  }
};

export const usePreSelectedObjectTags = () => {
  const {
    setFilters,
    hasPrefilledObjectTags,
    setHasPrefilledObjectTags,
    setPreSelectedObjectTags
  } = useSearchStore();

  const { data: dataObjects } = useGetObjectTypeTranslations({
    config: {
      enabled: true
    }
  });
  const dataObjectOptions = useGetDataObjectOptions(dataObjects);
  const preSelectedObjectTags = usePreSelectedWorkflowDataObjectTags();
  const excludedWorkflowDataObjectTags = useExcludedWorkflowDataObjectTags();

  if (
    !hasPrefilledObjectTags &&
    dataObjectOptions?.length > 0 &&
    preSelectedObjectTags.length > 0
  ) {
    const filteredPreSelectedObjectTags: string[] = dataObjectOptions
      .filter(
        option =>
          !excludedWorkflowDataObjectTags.includes(option.value) &&
          preSelectedObjectTags.includes(option.value)
      )
      .map(kind => kind.value);

    if (filteredPreSelectedObjectTags.length > 0) {
      setFilters({ dataObjectTags: filteredPreSelectedObjectTags });
    }

    setPreSelectedObjectTags(filteredPreSelectedObjectTags);

    // Mark as initialized so we don't run this logic again
    setHasPrefilledObjectTags(true);
  }
};
